import { useState } from "react";
import {
  ArrowUpRight,
  Check,
  Copy,
  GitBranch,
  Network,
  Plus,
  X,
} from "lucide-react";
import { api } from "../api";
import type { WorkflowSummary } from "../../shared/types";

interface WorkflowsViewProps {
  workflows: WorkflowSummary[];
  onOpen: (id: string) => void;
  onRefresh: () => Promise<WorkflowSummary[]>;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

export function WorkflowsView({ workflows, onOpen, onRefresh }: WorkflowsViewProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  async function createWorkflow() {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const result = await api.createWorkflow(name.trim());
      await onRefresh();
      setCreateOpen(false);
      setName("");
      onOpen(result.workflow.id);
    } finally {
      setCreating(false);
    }
  }

  async function copyWebhook(workflow: WorkflowSummary) {
    const url = `${window.location.origin}/webhooks/${workflow.webhookToken}`;
    await navigator.clipboard.writeText(url);
    setCopied(workflow.id);
    window.setTimeout(() => setCopied(null), 1800);
  }

  return (
    <main className="workspace-page">
      <header className="page-header">
        <div><p className="eyebrow">Biblioteca</p><h1>Workflows</h1><p>{workflows.length} fluxos no workspace</p></div>
        <button className="button button--primary" onClick={() => setCreateOpen(true)}><Plus size={15} />Novo workflow</button>
      </header>

      <section className="data-table workflow-table" aria-label="Workflows">
        <div className="data-table__head"><span>Nome</span><span>Status</span><span>Versão</span><span>Grafo</span><span>Atualizado</span><span /></div>
        {workflows.map((workflow) => (
          <div className="data-table__row" key={workflow.id}>
            <button className="table-primary" onClick={() => onOpen(workflow.id)}>
              <span className="table-icon"><Network size={16} /></span>
              <span><strong>{workflow.name}</strong><small>{workflow.slug}</small></span>
            </button>
            <span className={`status-badge status-badge--${workflow.status}`}>{workflow.status === "published" ? "Publicado" : "Rascunho"}</span>
            <span>v{workflow.version || "-"}</span>
            <span><GitBranch size={13} />{workflow.graph?.nodes.length ?? 0} nós</span>
            <time>{formatDate(workflow.updatedAt)}</time>
            <div className="table-actions">
              <button className="icon-button" title="Copiar webhook" aria-label="Copiar webhook" onClick={() => void copyWebhook(workflow)}>
                {copied === workflow.id ? <Check size={15} /> : <Copy size={15} />}
              </button>
              <button className="icon-button" title="Abrir editor" aria-label="Abrir editor" onClick={() => onOpen(workflow.id)}><ArrowUpRight size={16} /></button>
            </div>
          </div>
        ))}
      </section>

      {createOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setCreateOpen(false)}>
          <div className="modal modal--compact" role="dialog" aria-modal="true" aria-labelledby="new-workflow" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header"><div><p className="eyebrow">Novo</p><h2 id="new-workflow">Criar workflow</h2></div><button className="icon-button" aria-label="Fechar" onClick={() => setCreateOpen(false)}><X size={17} /></button></div>
            <label><span>Nome</span><input value={name} onChange={(event) => setName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void createWorkflow(); }} autoFocus placeholder="Ex.: Atendimento de novos leads" /></label>
            <div className="modal-actions"><button className="button button--secondary" onClick={() => setCreateOpen(false)}>Cancelar</button><button className="button button--primary" onClick={() => void createWorkflow()} disabled={!name.trim() || creating}><Plus size={15} />Criar e abrir</button></div>
          </div>
        </div>
      )}
    </main>
  );
}
